import type { Pool } from "pg";

import { SQL_AML_ORDER_MULTIPLE_FOR_SKU } from "./aml-self.js";
import type { SkuHoverHintFields } from "./types";

/** Max SKUs per `GET /api/mrp/sku-hover-hints` request. */
export const SKU_HOVER_HINTS_QUERY_MAX = 500;

/**
 * Parse `cpns` on `GET /api/mrp/sku-hover-hints`. Comma-separated only — finished SKUs may contain spaces.
 */
export function parseSkuHoverHintsCpnsQuery(raw: string | undefined): string[] {
  if (raw == null || raw.trim() === "") return [];
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .slice(0, SKU_HOVER_HINTS_QUERY_MAX);
}

/**
 * Megaventory-backed display fields for SKU hover hints (category, description, variant).
 */
export async function loadSkuHoverHints(pool: Pool, skus: string[]): Promise<Record<string, SkuHoverHintFields>> {
  const unique = [...new Set(skus.map((sku) => sku.trim()).filter(Boolean))];
  const out: Record<string, SkuHoverHintFields> = {};
  if (unique.length === 0) return out;

  const result = await pool.query<{
    sku: string;
    category: string | null;
    description: string | null;
    variant: string | null;
    mv_unit: string | null;
    supply_chain_notes: string | null;
    order_multiple: number | string;
  }>(
    `select public.sku.sku,
            nullif(trim(coalesce(public.mv_product_category.mv_category_name, '')), '') as category,
            nullif(trim(coalesce(public.sku.mv_description, '')), '') as description,
            nullif(trim(coalesce(public.sku.mv_variant, '')), '') as variant,
            nullif(trim(coalesce(public.sku.mv_unit, '')), '') as mv_unit,
            nullif(
              trim(
                coalesce(
                  public.sku.hub_supply_chain_notes,
                  public.sku.mv_supply_chain_notes,
                  ''
                )
              ),
              ''
            ) as supply_chain_notes,
            ${SQL_AML_ORDER_MULTIPLE_FOR_SKU} as order_multiple
       from public.sku
       left join public.mv_product_category
         on public.mv_product_category.mv_category_id = public.sku.mv_category_id
      where public.sku.sku = any($1::text[])`,
    [unique],
  );

  for (const row of result.rows) {
    out[row.sku] = {
      category: row.category,
      description: row.description,
      variant: row.variant,
      mv_unit: row.mv_unit,
      supply_chain_notes: row.supply_chain_notes,
      order_multiple: Math.max(1, Number(row.order_multiple)),
    };
  }
  return out;
}
